// Push Notification Manager for PWA
class PushNotificationManager {
  constructor() {
    this.permission = 'default';
    this.subscription = null;
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  // Request permission
  async requestPermission() {
    if (!this.isSupported()) {
      console.log('Notifications not supported');
      return false;
    }

    try {
      this.permission = await Notification.requestPermission();
      console.log('Notification permission:', this.permission);
      return this.permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Show local notification (no backend needed)
  showNotification(title, options = {}) {
    if (!this.isSupported() || this.permission !== 'granted') {
      console.log('Cannot show notification - permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/logo192_1.png',
      badge: '/logo192_1.png',
      vibrate: [200, 100, 200],
      tag: 'ogh-notification',
      requireInteraction: false,
      ...options
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Use service worker for better notification handling
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, defaultOptions);
      });
    } else {
      // Fallback to regular notification
      new Notification(title, defaultOptions);
    }
  }

  // Listen to database changes and show notifications
  setupDatabaseListener(db) {
    if (!db) return;

    // Listen for order status changes
    db.changes({
      since: 'now',
      live: true,
      include_docs: true
    }).on('change', (change) => {
      const doc = change.doc;
      
      // Order status changed
      if (doc.type === 'order' && doc.status) {
        this.handleOrderNotification(doc);
      }
      
      // Subscription renewal reminder
      if (doc.type === 'subscription' && doc.nextBillingDate) {
        this.handleSubscriptionNotification(doc);
      }
    }).on('error', (err) => {
      console.error('Database change listener error:', err);
    });
  }

  // Handle order notifications
  handleOrderNotification(order) {
    // Only notify the customer who owns this order
    const currentCustomerId = sessionStorage.getItem('customerId');
    if (order.customerId !== currentCustomerId) {
      return; // Don't show notification for other customers' orders
    }
    
    const statusMessages = {
      'confirmed': {
        title: '✅ Order Confirmed',
        body: `Your order has been confirmed!`
      },
      'out_for_delivery': {
        title: '🚚 Out for Delivery',
        body: `Your order is on the way!`
      },
      'delivered': {
        title: '📦 Order Delivered',
        body: `Your order has been delivered. Enjoy!`
      }
    };

    // Check both status and deliveryStatus fields
    const notification = statusMessages[order.status] || statusMessages[order.deliveryStatus];
    if (notification) {
      this.showNotification(notification.title, {
        body: notification.body,
        data: { orderId: order._id, type: 'order' }
      });
    }
  }

  // Handle subscription notifications
  handleSubscriptionNotification(subscription) {
    const daysUntilRenewal = this.getDaysUntilDate(subscription.nextBillingDate);
    
    if (daysUntilRenewal === 3) {
      this.showNotification('🔔 Subscription Renewal', {
        body: `Your subscription renews in 3 days. Ensure payment method is ready!`,
        data: { subscriptionId: subscription._id, type: 'subscription' }
      });
    }
  }

  // Calculate days until date
  getDaysUntilDate(dateString) {
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Manual notification triggers (call from your app)
  notifyOrderPlaced(orderId) {
    this.showNotification('🎉 Order Placed Successfully', {
      body: `Order #${orderId} has been placed. We'll notify you when it's confirmed.`,
      data: { orderId, type: 'order' }
    });
  }

  notifyDeliveryToday(orders) {
    if (orders.length > 0) {
      this.showNotification('📅 Delivery Today', {
        body: `You have ${orders.length} order(s) scheduled for delivery today!`,
        data: { type: 'delivery_reminder' }
      });
    }
  }

  notifyLowStock(productName) {
    this.showNotification('⚠️ Low Stock Alert', {
      body: `${productName} is running low. Order now!`,
      data: { type: 'stock_alert' }
    });
  }
}

// Export singleton instance
const pushNotificationManager = new PushNotificationManager();
export default pushNotificationManager;
